import { useMemo } from 'react';
import { categories, posts } from '@/mocks/posts';
import PostCard from '@/components/feature/PostCard';

interface LatestSectionProps {
  query: string;
  onQueryChange: (value: string) => void;
  activeCategory: string;
  onCategoryChange: (slug: string) => void;
}

export default function LatestSection({ query, onQueryChange, activeCategory, onCategoryChange }: LatestSectionProps) {
  const effectiveQuery = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    return posts.filter((post) => {
      const matchCategory = activeCategory === 'all' || post.categorySlug === activeCategory;
      if (!matchCategory) {
        return false;
      }
      if (!effectiveQuery) {
        return true;
      }
      const haystack = `${post.title}${post.excerpt}${post.tags.join('')}`.toLowerCase();
      return haystack.includes(effectiveQuery);
    });
  }, [activeCategory, effectiveQuery]);

  const tabs = [{ slug: 'all', name: '全部看点' }, ...categories.map((item) => ({ slug: item.slug, name: item.name }))];

  return (
    <section id="latest" className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100 scroll-mt-20">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 md:mb-10">
          <span className="inline-flex items-center gap-1.5 text-xs font-label tracking-[0.2em] text-primary-600 uppercase mb-3">
            <i className="ri-subtract-line" />
            最新合集
          </span>
          <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">按分类挑你想看的看点</h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-7">
            切换分类或直接搜索关键词，找到对应的精彩片段合集。
          </p>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 md:mb-8">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.slug}
                type="button"
                onClick={() => onCategoryChange(tab.slug)}
                className={`px-3.5 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  activeCategory === tab.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-700'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
            <label htmlFor="latest-search" className="sr-only">
              搜索最新合集
            </label>
            <input
              id="latest-search"
              type="search"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder="搜索合集关键词"
              className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        </div>

        {filtered.length > 0 ? (
          <>
            <p className="mb-5 text-xs text-foreground-500">共找到 {filtered.length} 期相关合集</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filtered.map((post) => (
                <PostCard key={post.id} post={post} layout="grid" />
              ))}
            </div>
          </>
        ) : (
          <div className="py-16 text-center bg-background-50 rounded-lg border border-background-200">
            <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-2xl">
              <i className="ri-search-eye-line" />
            </span>
            <h3 className="mt-4 font-heading text-xl text-foreground-900">暂时没有匹配的看点</h3>
            <p className="mt-2 text-sm text-foreground-500">换个关键词，或切换其他分类试试看。</p>
          </div>
        )}
      </div>
    </section>
  );
}